import { FC } from 'react'
import { DateRange, LocalizationProvider } from '@mui/x-date-pickers-pro'
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs'
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker'
import { TextField } from '@mui/material'
import dayjs, { Dayjs } from 'dayjs'
import { dateFormat } from '../../../../../lib/utils'
import { DateRangePickerContainer } from '../auditLog.styles'
import theme from 'theme'

interface Props {
  dateValues: DateRange<Dayjs>
  handleDateChange: (date: DateRange<Dayjs>) => Promise<void>
}

const AuditDatePicker: FC<Props> = ({ dateValues, handleDateChange }) => {
  return (
    <DateRangePickerContainer>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DateRangePicker
          calendars={1}
          value={dateValues}
          onAccept={(date) => {
            handleDateChange(date)
          }}
          slotProps={{
            popper: {
              sx: {
                '.MuiPaper-root.MuiPickersPopper-paper': {
                  borderRadius: theme.attributes.borderRadius.quaternary,
                  background: theme.colors.tooltipBg,

                  '.MuiPickersArrowSwitcher-root': {
                    '.MuiButtonBase-root': {
                      color: theme.colors.devicesSecondaryText
                    }
                  },

                  '.MuiPickersCalendarHeader-label': {
                    color: theme.colors.devicesSecondaryText,
                    fontFamily: theme.typography.fontFamily.primary,
                    fontSize: theme.typography.fontSize.large,
                    fontStyle: 'normal',
                    fontWeight: theme.typography.fontWeight.bold,
                    lineHeight: '20px',
                    letterSpacing: '0.1px'
                  },

                  '.MuiDayCalendar-header': {
                    '.MuiTypography-root.MuiTypography-caption.MuiDayCalendar-weekDayLabel': {
                      color: theme.colors.devicesSecondaryText,
                      textAlign: 'center',
                      fontFamily: theme.typography.fontFamily.primary,
                      fontSize: theme.typography.fontSize.xLarge,
                      fontStyle: 'normal',
                      fontWeight: theme.typography.fontWeight.bold,
                      lineHeight: '24px',
                      letterSpacing: '0.1px'
                    }
                  },

                  '.MuiButtonBase-root.MuiPickersDay-root': {
                    color: theme.colors.devicesSecondaryText,
                    textAlign: 'center',
                    fontFamily: theme.typography.fontFamily.primary,
                    fontSize: theme.typography.fontSize.xLarge,
                    fontStyle: 'normal',
                    fontWeight: theme.typography.fontWeight.bold,
                    lineHeight: '24px',
                    letterSpacing: '0.1px',

                    '&.Mui-selected': {
                      background: theme.colors.cta,
                      color: '#66006E',

                      "html[data-theme='light'] &": {
                        color: '#ffffff'
                      }
                    },
                    '&:not(.Mui-selected)': {
                      '&:hover': {
                        background: theme.colors.caption,
                        borderColor: theme.colors.caption,

                        "html[data-theme='light'] &": {
                          background: theme.colors.menuItemHover,
                          borderColor: theme.colors.menuItemHover
                        }
                      }
                    },
                    '&.MuiPickersDay-today': {
                      background: 'transparent',
                      border: `1px solid ${theme.colors.cta}`,
                      color: theme.colors.cta
                    },
                    '&.Mui-disabled': {
                      color: theme.colors.checkboxUnchecked
                    }
                  },

                  '.MuiButton-root': {
                    color: theme.colors.cta,
                    fontFamily: theme.typography.fontFamily.primary,
                    fontSize: theme.typography.fontSize.large,
                    lineHeight: '20px',
                    letterSpacing: '0.1px',
                    margin: 0
                  }
                }
              }
            },
            actionBar: {
              actions: ['cancel', 'accept']
            }
          }}
          format="MM/DD/YYYY"
          slots={{
            textField: (props) => {
              return (
                <>
                  <span>{props.label === 'Start' ? 'From' : 'To'}</span>
                  <TextField
                    className="text-field"
                    {...props}
                    label=""
                    value={`${dayjs((props as any).value).format(dateFormat.auditLogDefault)}`}
                  />
                </>
              )
            }
          }}
          showDaysOutsideCurrentMonth
          disableFuture
        />
      </LocalizationProvider>
    </DateRangePickerContainer>
  )
}

export default AuditDatePicker
